import React, { memo, useEffect, useState } from "react";
import {
  Accordion,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner
} from "react-bootstrap";
import InputMask from "react-input-mask";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import Select from "react-select";

import { Creators as NotificationsActions } from "./../../store/ducks/notifications";

import { isScopes } from "./../../services/Auth";
import { isCellphone } from "./../../utils/Cellphone";
import { isCPF, maskCPF } from "./../../utils/CPF";
import { maskCNPJ } from "./../../utils/CNPJ";
import { isEmail } from "./../../utils/Email";
import { isValidForm } from "./../../utils/Form";
import { clearObject, mergeObject } from "./../../utils/Object";
import { isPasswordPin } from "../../utils/Password";
import { isEmpty, ucFirstAll } from "./../../utils/String";
import { jsonToForm } from "./../../utils/User";

import { getDefaultOptionsPatrimony } from "./../../utils/Notification";
import FilterPatrimonyUser from "../../components/FilterPatrimonyUser";

import ClientService from "./../../services/Client";
import ClientSchemaReduced from "./../../services/Client/SchemaReduced";
import DriverService from "./../../services/Driver";
import DriverSchemaReduced from "./../../services/Driver/SchemaReduced";
import ProfileService from "./../../services/Profile";
import ProfileSchemaReduced from "./../../services/Profile/SchemaReduced";
import UserService from "./../../services/User";
import UserSchema from "./../../services/User/Schema";

const ACTIVE_KEY_CONTACT = "1";
const ACTIVE_KEY_GENERAL_DATA = "0";
const ACTIVE_KEY_PERMISSIONS = "2";
const ACTIVE_KEY_PATRIMONIES = "3";

const User = ({
  options,
  props: {
    currentUser,
    patrimonies,
    patrimoniesSearchSelected
  },
  funcs: { addNotice }
}) => {
  const { t } = useTranslation();
  const clientService = new ClientService();
  const driverService = new DriverService();
  const profileService = new ProfileService();
  const userService = new UserService();

  const {
    user: __user = {},
    close: __close = () => {}
  } = options;

  const [user, setUser] = useState(jsonToForm(mergeObject(JSON.parse(JSON.stringify(UserSchema)), {
    ...__user,
    password: ""
  })));

  const [newUser, setNewUser] = useState(jsonToForm(mergeObject(JSON.parse(JSON.stringify(UserSchema)), {
    ...__user,
    password: ""
  })));

  useEffect(() => {
    setTimeout(() => {
      if(!isScopes({ currentUser, scopes: [
        "is:master",
        "read:users:all"
      ], every: false })) {
        setClientSelectedOption({
          dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client),
          label: currentUser.client.type === "LEG" ? (`${currentUser.client.legal.socialReason} | CNPJ: ${maskCNPJ(currentUser.client.legal.cnpj)}`) : (`${currentUser.client.personal.name} | CPF: ${maskCPF(currentUser.client.personal.cpf)}`),
          value: currentUser.client.id
        });
        setUser(prevState => ({
          ...prevState,
          client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client)
        }));
        setNewUser(prevState => ({
          ...prevState,
          client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client)
        }));
      }
    }, 1000);
  }, []); /* eslint-disable-line */

  const [activeKey, setActiveKey] = useState("0");
  const [patrimoniesOptions, setPatrimoniesOptions] = useState({ /* eslint-disable-line */
    ...getDefaultOptionsPatrimony(),
    filterMultiple: true,
    filterPreSelected: user.patrimonies ?? []
  });

  const [activeOptions] = useState([
    { value: true, label: t("Active") },
    { value: false, label: t("Inactive") }
  ]);

  const [userFirstLoginPasswordOptions] = useState([
    { value: true, label: t("Change") },
    { value: false, label: t("Keep") }
  ]);

  const [userPasswordOptions] = useState([
    { value: true, label: t("Change") },
    { value: false, label: t("Keep") }
  ]);

  const [userPasswordPinOptions] = useState([
    { value: true, label: t("Change") },
    { value: false, label: t("Keep") }
  ]);

  const [changePassword, setChangePassword] = useState(false);
  const [changePasswordPin, setChangePasswordPin] = useState(false);

  const [clientSelectedOption, setClientSelectedOption] = useState(user.client.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), user.client),
    label: user.client.type === "LEG" ? (`${user.client.legal.socialReason} | CNPJ: ${maskCNPJ(user.client.legal.cnpj)}`) : (`${user.client.personal.name} | CPF: ${maskCPF(user.client.personal.cpf)}`),
    value: user.client.id
  });

  const [driverSelectedOption, setDriverSelectedOption] = useState(user.driver.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(DriverSchemaReduced)), user.driver),
    label: user.driver.name,
    value: user.driver.id
  });

  const [profileSelectedOption, setProfileSelectedOption] = useState(user.profilePredefined.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(ProfileSchemaReduced)), user.profilePredefined),
    label: user.profilePredefined.name,
    value: user.profilePredefined.id
  });

  const [running, setRunning] = useState(false);

  const [patrimoniesSelected, setPatrimoniesSelected] = useState(user.patrimonies ?? []);

  const buttonDisabled = () => {
    if(!running) {
      return false;
    }
    return true;
  };

  const changeActiveKey = (e) => {
    if(activeKey === e) {
      return setActiveKey("-1");
    }
    setActiveKey(e);
  };

  const clientLoadOptions = (e, c) => {
    clientLoadOptionsDebounce(e, c);
  };

  const clientLoadOptionsDebounce = debounce(async (e, c) => {
    const clients = await clientService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterClient(clients));
  }, 1000);

  const filterClient = (clients) => {
    return clients.map(e => {
      return {
        dataAux: e,
        label: e.type === "LEG" ? (`${e.legal.socialReason} | CNPJ: ${maskCNPJ(e.legal.cnpj)}`) : (`${e.personal.name} | CPF: ${maskCPF(e.personal.cpf)}`),
        value: e.id
      };
    });
  };

  const driverLoadOptions = (e, c) => {
    driverLoadOptionsDebounce(e, c);
  };

  const driverLoadOptionsDebounce = debounce(async (e, c) => {
    const drivers = await driverService.findAutocomplete({ data: {
      search: e,
      _user: true
    }, token: currentUser.token });
    c(filterDriver(drivers));
  }, 1000);

  const filterDriver = (drivers) => {
    return drivers.map(e => {
      return {
        dataAux: e,
        label: e.name,
        value: e.id
      };
    });
  };

  const filterProfile = (profiles) => {
    return profiles.map(e => {
      return {
        dataAux: e,
        label: e.name,
        value: e.id
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if(!running) {
      setRunning(true);
      let { eventKey, message, valid } = isValidForm(e);
      if(!valid) {
        if(!message) {
          message = t("Error.Fields.Empty");
        }
        if(eventKey !== -1) {
          setActiveKey(eventKey);
        }
        addNotice({
          title: t("Title.User"),
          content: message
        });
      }
      else {
        let response = false;
        if(isCreate()) {
          user._driver = true;
          response = await userService.create({
            data: handleSubmitForm({ user }),
            token: currentUser.token
          });
        }
        else {
          user._driver = true;
          response = await userService.update({
            id: user.id,
            data: handleSubmitForm({ user }),
            token: currentUser.token
          });
        }

        if(response.success) {
          addNotice({
            title: t("Title.User"),
            content: isCreate() ? t("Success.Insert") : t("Success.Update")
          });
          setTimeout(() => {
            __close();
          }, 200);
        }
        else {
          addNotice({
            title: t("Title.User"),
            content: t(`Error.User.${response.error.type}.${ucFirstAll(response.error.details[0].path)}`)
          });
        }
      }
    }
    setRunning(false);
  };

  const handleSubmitForm = ({ user }) => {
    let newUser = Object.assign({}, JSON.parse(JSON.stringify(user)));
    newUser = clearObject({ data: newUser });
    newUser.id = undefined;
    newUser.patrimonies = patrimoniesSelected ? patrimoniesSelected.map(user => user.id) : [];
    return newUser;
  };

  const isChangePassword = () => {
    return changePassword;
  };

  const isChangePasswordPin = () => {
    return changePasswordPin;
  };

  const inputDisabled = () => {
    if(!running) {
      return false;
    }
    return true;
  };

  const isCreate = () => {
    return user.id === "";
  };

  const profileLoadOptions = (e, c) => {
    profileLoadOptionsDebounce(e, c);
  };

  const profileLoadOptionsDebounce = debounce(async (e, c) => {
    const profiles = await profileService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterProfile(profiles));
  }, 1000);

  useEffect(() => {
    console.log(patrimoniesSelected);
  }, [patrimoniesSelected]);

  return (
    <Form id="forms-user" noValidate onSubmit={handleSubmit}>
      <Accordion defaultActiveKey={ACTIVE_KEY_GENERAL_DATA} activeKey={activeKey}>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_GENERAL_DATA} onClick={e => changeActiveKey(ACTIVE_KEY_GENERAL_DATA)}>{t("Title.GeneralData")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_GENERAL_DATA}>
            <Card.Body>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-user-active">
                    <Form.Label>{t("Label.Active")}:</Form.Label>
                    <Select
                      className="menu-outer-bottom"
                      classNamePrefix="select"
                      isDisabled={inputDisabled() || currentUser.id === user.id}
                      onChange={e => {
                        const active = e.value;
                        setUser(prevState => ({
                          ...prevState,
                          active
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      options={activeOptions}
                      value={activeOptions.find(x => x.value === user.active)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-user-client">
                    <Form.Label>{t("Title.Client")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(user.client.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled() || !isScopes({
                        currentUser,
                        scopes: [
                          "is:master",
                          "read:users:all"
                        ],
                        every: false
                      })}
                      loadOptions={clientLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if(e === null) {
                          setClientSelectedOption(e);
                          setUser(prevState => ({
                            ...prevState,
                            client: JSON.parse(JSON.stringify(ClientSchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setClientSelectedOption({ dataAux, label, value });
                          setUser(prevState => ({
                            ...prevState,
                            client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      placeholder={t("Title.Client.Select")}
                      value={clientSelectedOption}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(user.client.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Client")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Form.Group controlId="forms-user-driver">
                    <Form.Label>{t("Title.Driver")}:</Form.Label>
                    <AsyncSelect
                      className="menu-outer-bottom"
                      classNamePrefix="select"
                      cacheOptions
                      defaultOptions
                      isClearable
                      isDisabled={inputDisabled()}
                      loadOptions={driverLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if (e === null) {
                          setDriverSelectedOption(e);
                          setUser(prevState => ({
                            ...prevState,
                            driver: JSON.parse(JSON.stringify(DriverSchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setDriverSelectedOption({ dataAux, label, value });
                          setUser(prevState => ({
                            ...prevState,
                            driver: mergeObject(JSON.parse(JSON.stringify(DriverSchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      placeholder={t("Title.Driver.Select")}
                      value={driverSelectedOption}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-user-name">
                    <Form.Label>{t("Label.Name")}:</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t("Placeholder.Name")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const name = e.target.value;
                        setUser(prevState => ({
                          ...prevState,
                          name: name.toUpperCase()
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      autoComplete="off"
                      value={user.name}
                      isValid={!isEmpty(user.name)}
                      isInvalid={isEmpty(user.name)}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Name")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-user-cpf">
                    <Form.Label>{t("Label.CPF")}:</Form.Label>
                    <InputMask
                      id="forms-user-cpf"
                      className={`form-control ${!isEmpty(user.cpf) && isCPF(user.cpf) ? "is-valid" : "is-invalid"}`}
                      type="text"
                      placeholder={t("Placeholder.CPF")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const cpf = e.target.value;
                        setUser(prevState => ({
                          ...prevState,
                          cpf
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      mask="999.999.999-99"
                      autoComplete="off"
                      defaultValue={user.cpf}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">
                      {
                        isEmpty(user.cpf) ? (
                          t("Feedback.CPF")
                        ) : (
                          t("Feedback.Format.CPF")
                        )
                      }
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <div className={`${(!isCreate()) ? "" : "hide"}`}>
                <Row>
                  <Col>
                    <Form.Group controlId="forms-user-change-password">
                      <Form.Label>{t("Label.Password")}:</Form.Label>
                      <Select
                        className="menu-outer-top"
                        classNamePrefix="select"
                        isDisabled={inputDisabled()}
                        onChange={e => {
                          const { value } = e;
                          setUser(prevState => ({
                            ...prevState,
                            firstLogin: {
                              ...prevState.firstLogin,
                              password: value ? false : newUser.firstLogin.password
                            }
                          }));
                          setChangePassword(value);
                        }}
                        dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                        options={userPasswordOptions}
                        value={userPasswordOptions.find(x => x.value === changePassword)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
              <div className={`${(isChangePassword() || isCreate()) ? "" : "hide"}`}>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group controlId="forms-user-password">
                      <Form.Label>{t("Label.Password")}:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t("Placeholder.Password")}
                        disabled={inputDisabled()}
                        onChange={e => {
                          const password = e.target.value;
                          setUser(prevState => ({
                            ...prevState,
                            password
                          }));
                        }}
                        dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                        autoComplete="off"
                        defaultValue={user.password}
                        isValid={(isChangePassword() || isCreate()) ? !isEmpty(user.password) : false}
                        isInvalid={(isChangePassword() || isCreate()) ? isEmpty(user.password) : false}
                        required={(isChangePassword() || isCreate()) ? true : false}
                      />
                      <Form.Control.Feedback type="invalid">{t("Feedback.Password")}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group controlId="forms-user-first-login-password">
                      <Form.Label>{t("Label.FirstLoginPassword")}:</Form.Label>
                      <Select
                        className="menu-outer-top"
                        classNamePrefix="select"
                        isDisabled={inputDisabled()}
                        onChange={e => {
                          const password = e.value;
                          setUser(prevState => ({
                            ...prevState,
                            firstLogin: {
                              ...prevState.firstLogin,
                              password
                            }
                          }));
                        }}
                        dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                        options={userFirstLoginPasswordOptions}
                        value={userFirstLoginPasswordOptions.find(x => x.value === user.firstLogin.password)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
              <div className={`${(!isCreate()) ? "" : "hide"}`}>
                <Row>
                  <Col>
                    <Form.Group controlId="forms-user-change-password-pin">
                      <Form.Label>{t("Label.PasswordPin")}:</Form.Label>
                      <Select
                        className="menu-outer-top"
                        classNamePrefix="select"
                        isDisabled={inputDisabled()}
                        onChange={e => {
                          const { value } = e;
                          setChangePasswordPin(value);
                        }}
                        dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                        options={userPasswordPinOptions}
                        value={userPasswordPinOptions.find(x => x.value === changePasswordPin)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
              <div className={`${(isChangePasswordPin() || isCreate()) ? "" : "hide"}`}>
                <Row>
                  <Col>
                    <Form.Group controlId="forms-user-password-pin">
                      <Form.Label>{t("Label.PasswordPin")}:</Form.Label>
                      <InputMask
                        id="forms-user-password-pin"
                        className={`form-control ${(isChangePasswordPin() || isCreate()) ? (!isEmpty(user.passwordPin) && isPasswordPin(user.passwordPin) ? "is-valid" : "is-invalid") : ""}`}
                        type="text"
                        placeholder={t("Placeholder.PasswordPin")}
                        disabled={inputDisabled()}
                        onChange={e => {
                          const passwordPin = e.target.value;
                          setUser(prevState => ({
                            ...prevState,
                            passwordPin
                          }));
                        }}
                        dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                        mask="9999"
                        autoComplete="off"
                        defaultValue={user.passwordPin}
                        required={(isChangePasswordPin() || isCreate()) ? true : false}
                      />
                      <Form.Control.Feedback type="invalid">
                        {
                          ((isChangePasswordPin() || isCreate()) && isEmpty(user.passwordPin) && (
                            t("Feedback.PasswordPin")
                          ))
                        }
                        {
                          ((isChangePasswordPin() || isCreate()) && !isEmpty(user.passwordPin) && !isPasswordPin(user.passwordPin) && (
                            t("Feedback.Format.PasswordPin")
                          ))
                        }
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_CONTACT} onClick={e => changeActiveKey(ACTIVE_KEY_CONTACT)}>{t("Title.Contact")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_CONTACT}>
            <Card.Body>
              <Row>
                <Col>
                  <Form.Group controlId="forms-user-contact-email">
                    <Form.Label>{t("Label.Email")}:</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder={t("Placeholder.Email")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const email = e.target.value;
                        setUser(prevState => ({
                          ...prevState,
                          contact: {
                            ...prevState.contact,
                            email: email.toLowerCase()
                          }
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_CONTACT}
                      autoComplete="off"
                      value={user.contact.email}
                      isValid={!isEmpty(user.contact.email) && isEmail(user.contact.email)}
                      isInvalid={isEmpty(user.contact.email) || !isEmail(user.contact.email)}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">
                      {
                        isEmpty(user.contact.email) ? (
                          t("Feedback.Email")
                        ) : (
                          t("Feedback.Format.Email")
                        )
                      }
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-user-contact-telephone">
                    <Form.Label>{t("Label.Telephone")}:</Form.Label>
                    <InputMask
                      id="forms-user-contact-telephone"
                      className={`form-control ${!isEmpty(user.contact.telephone) ? (isCellphone(user.contact.telephone) ? "is-valid" : "is-invalid") : ""}`}
                      type="text"
                      placeholder={t("Placeholder.Telephone")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const telephone = e.target.value;
                        setUser(prevState => ({
                          ...prevState,
                          contact: {
                            ...prevState.contact,
                            telephone
                          }
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_CONTACT}
                      mask="(99) 999999999"
                      autoComplete="off"
                      defaultValue={user.contact.telephone}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Format.Telephone")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-user-contact-cellphone">
                    <Form.Label>{t("Label.Cellphone")}:</Form.Label>
                    <InputMask
                      id="forms-user-contact-cellphone"
                      className={`form-control ${!isEmpty(user.contact.cellphone) ? (isCellphone(user.contact.cellphone) ? "is-valid" : "is-invalid") : ""}`}
                      type="text"
                      placeholder={t("Placeholder.Cellphone")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const cellphone = e.target.value;
                        setUser(prevState => ({
                          ...prevState,
                          contact: {
                            ...prevState.contact,
                            cellphone
                          }
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_CONTACT}
                      mask="(99) 999999999"
                      autoComplete="off"
                      defaultValue={user.contact.cellphone}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Format.Cellphone")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_PERMISSIONS} onClick={e => changeActiveKey(ACTIVE_KEY_PERMISSIONS)}>{t("Title.Permissions")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_PERMISSIONS}>
            <Card.Body>
              <Row>
                <Col>
                  <Form.Group controlId="forms-user-profile">
                    <Form.Label>{t("Title.Profile")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-top ${!isEmpty(user.profilePredefined.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled()}
                      loadOptions={profileLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if(e === null) {
                          setProfileSelectedOption(e);
                          setUser(prevState => ({
                            ...prevState,
                            profile: {
                              ...prevState.profile,
                              notification: {
                                ...prevState.profile.notification,
                                group: []
                              }
                            },
                            profilePredefined: JSON.parse(JSON.stringify(ProfileSchemaReduced)),
                            scopes: []
                          }));
                        }
                        else {
                          let group = isCreate() ? [] : user.profile.notification.group;
                          const { dataAux, label, value } = e;
                          setProfileSelectedOption({ dataAux, label, value });

                          if(isCreate()) {
                            dataAux.scopes.filter(x => x.scope.indexOf("notifications:") !== -1).map(scope => {
                              return group.push({
                                patrimonies: [],
                                scope: scope.scope
                              });
                            });
                          }
                          else {
                            group = group.filter(x => dataAux.scopes.findIndex(y => y.scope === x.scope) !== -1);
                          }

                          setUser(prevState => ({
                            ...prevState,
                            profile: {
                              ...prevState.profile,
                              notification: {
                                ...prevState.profile.notification,
                                group
                              }
                            },
                            profilePredefined: mergeObject(JSON.parse(JSON.stringify(ProfileSchemaReduced)), dataAux),
                            scopes: dataAux.scopes
                          }));
                        }
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      placeholder={t("Title.Profile.Select")}
                      value={profileSelectedOption}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(user.profilePredefined.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Profile")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_PATRIMONIES} onClick={e => changeActiveKey(ACTIVE_KEY_PATRIMONIES)}>{t("Title.Patrimony")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_PATRIMONIES}>
            <Card.Body>
              <Row>
                <Col>
                  <Form.Group controlId="forms-user-patrimonies">
                    {(clientSelectedOption !== null && profileSelectedOption !== null) ? null : (<Form.Label>Selecione o cliente e o perfil primeiro</Form.Label>)}
                    {
                      ((clientSelectedOption !== null && profileSelectedOption !== null) && (
                        <FilterPatrimonyUser options={{...patrimoniesOptions, client: clientSelectedOption.value, scopes: isScopes({ currentUser: user, scopes: ["is:master"], every: false }), setPatrimoniesSelected }} />
                      ))
                    }
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <Form.Group className="default-form-button">
        <Button
          variant="dark"
          type="submit"
          disabled={buttonDisabled()}
        >
          {running ? <Spinner animation="border" size="sm" /> : t("Button.Save")}
        </Button>
      </Form.Group>
    </Form>
  );
};

const mapStateToProps = state => ({
  props: {
    currentUser: state.users.currentUser,
    patrimonies: state.patrimonies.patrimonies,
    patrimoniesSearchSelected: state.patrimonies.patrimoniesSearchSelected
  }
});

const mapDispatchToProps = dispatch => ({
  funcs: bindActionCreators(Object.assign({}, NotificationsActions), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(User));
